import React, { useEffect, useState } from 'react';
import { getBreedOptions, OTHER_BREED } from '@rehome/shared';
import type { Species } from '@rehome/shared';

const SPECIES_OPTIONS: Species[] = ['Kutya', 'Macska', 'Egyéb'];

interface SpeciesBreedSelectProps {
  species: string;
  breed: string;
  onSpeciesChange: (species: string) => void;
  onBreedChange: (breed: string) => void;
  /** Tailwind classes applied to both <select>/<input> elements — pass the caller's existing input style. */
  inputClassName?: string;
  labelClassName?: string;
}

/**
 * Faj → fajta lecsatolt választás. Kutya/Macska esetén kurált legördülő
 * lista (+ "Keverék" és "Egyéb" szabad szöveges fallback); "Egyéb" fajnál
 * mindig szabad szöveges fajta input (nincs kurált lista nyulakhoz, madarakhoz stb.).
 */
export const SpeciesBreedSelect: React.FC<SpeciesBreedSelectProps> = ({
  species, breed, onSpeciesChange, onBreedChange,
  inputClassName = 'w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white',
  labelClassName = 'block text-sm font-medium text-gray-700 mb-1',
}) => {
  const breedOptions = getBreedOptions(species);

  // Whether the free-text "custom breed" input is shown for the dropdown
  // species (Kutya/Macska). Tracked as its own state (not purely derived)
  // because selecting "Egyéb" clears `breed` to '' — without this we'd lose
  // track of "user is mid-typing a custom breed" the moment the text is empty.
  const [customMode, setCustomMode] = useState(() => breed !== '' && !breedOptions.includes(breed));

  // Re-sync when a different animal's data loads into the form (edit pages).
  useEffect(() => {
    setCustomMode(breed !== '' && !getBreedOptions(species).includes(breed));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [species]);

  const isFreeTextSpecies = species === 'Egyéb';

  const handleSpeciesChange = (newSpecies: string) => {
    onSpeciesChange(newSpecies);
    onBreedChange(''); // reset breed — the old value likely doesn't belong to the new species' list
    setCustomMode(false);
  };

  const handleBreedSelect = (value: string) => {
    if (value === OTHER_BREED) {
      setCustomMode(true);
      onBreedChange('');
    } else {
      setCustomMode(false);
      onBreedChange(value);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className={labelClassName}>Faj</label>
        <select value={species} onChange={e => handleSpeciesChange(e.target.value)} className={inputClassName}>
          {SPECIES_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className={labelClassName}>Fajta</label>
        {isFreeTextSpecies ? (
          <input
            type="text" value={breed} onChange={e => onBreedChange(e.target.value)}
            className={inputClassName} placeholder="pl. Nyúl, papagáj..."
          />
        ) : (
          <>
            <select
              value={customMode ? OTHER_BREED : breed}
              onChange={e => handleBreedSelect(e.target.value)}
              className={inputClassName}
            >
              <option value="">— Válassz —</option>
              {breedOptions.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            {customMode && (
              <input
                type="text" value={breed} onChange={e => onBreedChange(e.target.value)}
                className={`${inputClassName} mt-2`} placeholder="Add meg a fajtát..." autoFocus
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SpeciesBreedSelect;
